import React from 'react';
import styled from 'styled-components';
import moment from 'moment-timezone';
import { dateHasOpenHours, hasOnlineWaitlistAvailability } from '../../core/util/location';
import {
  generateSlotsFromOperatingHours,
  getSlotsForTodayAndTomorrow,
  hasSlotsAvailable,
  isReservationsRemaining,
  removeSlotsBeforeDisableReservationsUntilTimestamp,
} from '../../core/location/slots';
import { isDateAfterTomorrow, isDateToday, isDateTomorrow } from '../../core/util/date';
import {
  MORE_DATES_SLOT_SELECTOR,
  NEXT_DAY_BOOKINGS_SLOT_SELECTOR,
  shouldShowFeature,
} from '../../core/util/customization';

import Loading from '../SolvPatternLibrary/Loading';
import { colors } from '../../constants/colors';
import { isEmptyArray } from '../../core/util/array';
import { isEmpty } from '../../core/util/empty';
import { fetchSlotsForDate } from '../../core/dapi/location';

const SpacedLoading = styled.div.withConfig({ componentId: 'sc-7e9cao-0' })`
  text-align: center;
  height: 255px;
  align-items: center;
  justify-content: center;
  display: flex;
`;

const SlotLoading = styled(SpacedLoading).withConfig({ componentId: 'sc-7e9cao-1' })`
  height: 117px;
`;

const popFirstUnavailableSlots = (slots: any) => {
  for (let i = 0; i < slots.length; i++) {
    if (slots[i].availability !== 0) {
      return slots.slice(i);
    }
  }

  return [];
};

const getSlotDataForDate = (futureSlotData: any, selectedDate: any) => {
  return selectedDate && futureSlotData[selectedDate.format('YYYY-MM-DD')];
};

const getSelectedSlot = ({ selectedSlots, location }: any) =>
  !isEmpty(selectedSlots) && moment.tz(selectedSlots[0].appointmentDate, location.timeZone);

const getCurrentStateOfLocationSlots = (futureSlotData: any, location: any, selectedDate: any) => {
  const isClinicClosed = !dateHasOpenHours(location, selectedDate);
  const todayAndTomorrowSlots = getSlotsForTodayAndTomorrow(location);
  const todaySlots = removeSlotsBeforeDisableReservationsUntilTimestamp(
    popFirstUnavailableSlots(todayAndTomorrowSlots.today),
    location
  );
  const tomorrowSlots = popFirstUnavailableSlots(todayAndTomorrowSlots.tomorrow);

  let selectedDateSlots = null;
  let nextDateSlots = null;
  let moreDateOverride = false;

  if (isDateToday(selectedDate, location.timeZone)) {
    selectedDateSlots = todaySlots;
    nextDateSlots = tomorrowSlots;
  } else if (isDateTomorrow(selectedDate, location.timeZone)) {
    selectedDate.startOf('day');
    selectedDateSlots = tomorrowSlots;
    nextDateSlots = null;
  } else if (shouldShowFeature(MORE_DATES_SLOT_SELECTOR, location)) {
    const selectedDateSlotData = getSlotDataForDate(futureSlotData, selectedDate);

    if (selectedDateSlotData && selectedDateSlotData !== 'loading') {
      selectedDateSlots = popFirstUnavailableSlots(selectedDateSlotData);
      moreDateOverride = true;
    } else
      return (
        <SlotLoading>
          <Loading color={colors.crunchBerry} />
        </SlotLoading>
      );
  } else {
    selectedDateSlots = generateSlotsFromOperatingHours(selectedDate, location);
    moreDateOverride = true;
  }

  const selectedDateHasNoSlotsLeft =
    !hasSlotsAvailable(selectedDateSlots) ||
    (!moreDateOverride && !isReservationsRemaining(location, selectedDate));

  return {
    isClinicClosed,
    nextDateSlots,
    selectedDateHasNoSlotsLeft,
    selectedDateSlots,
  };
};

const setAndHandleDateFromCurrentTab = (
  currentTabIndex: any,
  timeZone: any,
  setAndHandleDate: any,
  setIsMoreDate: any
) => {
  let selectedDate = null;
  let nextDate = null;
  const isMoreDate = false;
  if (currentTabIndex === 0) {
    selectedDate = moment.tz(timeZone);
    nextDate = moment.tz(timeZone).add(1, 'day');
  } else if (currentTabIndex === 1) {
    selectedDate = moment.tz(timeZone).add(1, 'day');
    nextDate = moment.tz(timeZone).add(2, 'day');
  } else {
    return setIsMoreDate(true);
  }

  return setAndHandleDate({
    selectedDate,
    nextDate,
    isMoreDate,
  });
};

const setSlotDataForDate = ({
  futureSlotData,
  selectedDate,
  setFutureSlotData,
  slots,
  callback,
}: any) => {
  setFutureSlotData({ ...futureSlotData, [selectedDate.format('YYYY-MM-DD')]: slots });
  callback && callback();
};

const fetchSlotDataForDate = ({
  futureSlotData,
  location,
  selectedDate,
  setFutureSlotData,
  callback,
}: any) => {
  if (!getSlotDataForDate(futureSlotData, selectedDate)) {
    fetchSlotsForDate(location, selectedDate)
      .then((slots) =>
        setSlotDataForDate({ futureSlotData, selectedDate, setFutureSlotData, slots, callback })
      )
      .catch(console.error);
  }
};

const isSelectedSlotAfterTomorrow = (
  selectedSlot: moment.Moment,
  tomorrow: moment.Moment
): boolean => selectedSlot.isAfter(tomorrow, 'day');

const shouldDefaultToNextDay = (location: any, today: moment.Moment, todaySlots: any[]): boolean =>
  !hasOnlineWaitlistAvailability(location) &&
  (!dateHasOpenHours(location, today) ||
    (!hasSlotsAvailable(todaySlots) &&
      shouldShowFeature(NEXT_DAY_BOOKINGS_SLOT_SELECTOR, location)));

const updateSelectedAndNextDates = ({
  location,
  selectedSlots,
  setSelectedDate,
  setNextDate,
  setIsMoreDate,
}: any) => {
  const today = moment.tz(location.timeZone);
  const todayAndTomorrowSlots = getSlotsForTodayAndTomorrow(location);
  const todaySlots = popFirstUnavailableSlots(todayAndTomorrowSlots.today);
  const firstSelectedSlot = getSelectedSlot({ selectedSlots, location });

  if (
    !isEmptyArray(selectedSlots) &&
    !hasOnlineWaitlistAvailability(location) &&
    firstSelectedSlot
  ) {
    const tomorrow = moment.tz(location.timeZone).add(1, 'day');
    const isMoreDate = isSelectedSlotAfterTomorrow(firstSelectedSlot, tomorrow);

    setSelectedDate(firstSelectedSlot);
    setNextDate(firstSelectedSlot.clone().add(1, 'day'));
    setIsMoreDate(isMoreDate);
  } else if (shouldDefaultToNextDay(location, today, todaySlots)) {
    setSelectedDate(moment.tz(location.timeZone).add(1, 'day'));
    setNextDate(moment.tz(location.timeZone).add(2, 'day'));
  } else {
    setSelectedDate(today);
    setNextDate(moment.tz(location.timeZone).add(1, 'day'));
  }
};

const setAndHandleDate = ({
  futureSlotData,
  setFutureSlotData,
  isMoreDate,
  setIsMoreDate,
  selectedDate,
  setSelectedDate,
  nextDate,
  setNextDate,
  location,
}: any) => {
  selectedDate &&
    isDateAfterTomorrow(selectedDate, location.timeZone) &&
    shouldShowFeature(MORE_DATES_SLOT_SELECTOR, location) &&
    fetchSlotDataForDate({ futureSlotData, location, selectedDate, setFutureSlotData });

  selectedDate && setSelectedDate(selectedDate);
  nextDate && setNextDate(nextDate);
  !isEmpty(isMoreDate) && setIsMoreDate(isMoreDate);
};

export {
  fetchSlotDataForDate,
  updateSelectedAndNextDates,
  popFirstUnavailableSlots,
  setSlotDataForDate,
  setAndHandleDate,
  setAndHandleDateFromCurrentTab,
  fetchSlotsForDate,
  getCurrentStateOfLocationSlots,
  getSlotDataForDate,
  getSelectedSlot,
};
